import { Controller } from "@hotwired/stimulus"
import { showToast } from "../lib/platform_toast"
import { NativeBridge } from "../lib/native_bridge"
import { prefersReducedMotion } from "../lib/motion"

const BURST_COLORS = [ "#F57400", "#FF9E45", "#FFC400", "#EF4444" ]

/**
 * BookmarkToastController - Platform-aware toast for bookmark actions
 *
 * iOS app: Uses native Toast (keyWindow.addSubview for proper z-ordering)
 * Web/Android: Uses web Toast (Turbo Stream via ToastController)
 *
 * This controller connects to bookmark buttons and shows appropriate toast
 * after successful Turbo Stream form submission.
 *
 * Usage:
 *   <%= button_to bookmarks_path, data: {
 *     controller: "bookmark-toast",
 *     action: "turbo:submit-end->bookmark-toast#showToast",
 *     bookmark_toast_message_value: t("bookmarks.added")
 *   } do %>
 */
export default class extends Controller {
  static values = {
    message: { type: String, default: "" },
    variant: { type: String, default: "success" },
    duration: { type: Number, default: 3000 }
  }

  /**
   * Called after Turbo form submission completes.
   * Shows native toast on iOS, web toast on Web/Android.
   *
   * Uses platform_toast utility for consistent platform-aware behavior.
   *
   * @param {CustomEvent} event - turbo:submit-end event
   */
  // The heart answers the tap before the server does: adding fills and pops it with a small burst, removing
  // deflates it. The burst lives in a body-level layer, so the server's button replacement can't cut it off.
  optimistic() {
    const button = this.element.querySelector("button")
    const heart = button?.querySelector("svg")
    if (!heart) return

    this.adding = this.element.querySelector('[name="_method"]')?.value !== "delete"
    this.heart = heart
    this.previousFill = heart.getAttribute("fill")
    if (this.adding) heart.setAttribute("fill", "currentColor")
    if (prefersReducedMotion() || typeof heart.animate !== "function") return

    heart.animate(
      this.adding
        ? [ { transform: "scale(1)" }, { transform: "scale(1.45)" }, { transform: "scale(0.92)" }, { transform: "scale(1)" } ]
        : [ { transform: "scale(1)" }, { transform: "scale(0.7)" }, { transform: "scale(1)" } ],
      { duration: this.adding ? 480 : 260, easing: "cubic-bezier(0.23, 1, 0.32, 1)" }
    )
    if (this.adding) this.burst(heart.getBoundingClientRect())
  }

  burst(rect) {
    const layer = document.createElement("div")
    layer.setAttribute("aria-hidden", "true")
    layer.style.cssText = `position:fixed;left:${rect.left + rect.width / 2}px;top:${rect.top + rect.height / 2}px;` +
      "width:0;height:0;pointer-events:none;z-index:var(--z-toast)"
    document.body.appendChild(layer)
    const count = 10
    for (let i = 0; i < count; i++) {
      const dot = document.createElement("span")
      const size = i % 3 === 0 ? 7 : 5
      dot.style.cssText = `position:absolute;left:${-size / 2}px;top:${-size / 2}px;width:${size}px;height:${size}px;` +
        `border-radius:9999px;background:${BURST_COLORS[i % BURST_COLORS.length]}`
      layer.appendChild(dot)
      const angle = (i / count) * Math.PI * 2 + Math.random() * 0.4
      const distance = 22 + Math.random() * 14
      dot.animate(
        [ { transform: "translate(0,0) scale(0.4)", opacity: 1 },
          { transform: `translate(${Math.cos(angle) * distance}px, ${Math.sin(angle) * distance}px) scale(1)`, opacity: 1, offset: 0.6 },
          { transform: `translate(${Math.cos(angle) * distance * 1.25}px, ${Math.sin(angle) * distance * 1.25}px) scale(0)`, opacity: 0 } ],
        { duration: 560, easing: "cubic-bezier(0.23, 1, 0.32, 1)", fill: "forwards" }
      )
    }
    setTimeout(() => layer.remove(), 620)
  }

  async showToast(event) {
    // Only show toast on successful submission; a failed toggle puts the heart back.
    if (!event.detail.success) {
      if (this.heart && this.adding) {
        if (this.previousFill === null) this.heart.removeAttribute("fill")
        else this.heart.setAttribute("fill", this.previousFill)
      }
      return
    }

    const message = this.messageValue || this._getDefaultMessage()
    const variant = this.variantValue
    const duration = this.durationValue

    // Haptic feedback: selection tick for bookmark toggle
    NativeBridge.Haptic.selection()

    // Platform-aware toast: iOS uses native, Web/Android uses web toast
    await showToast(message, variant, duration)
  }

  /**
   * Get default message based on bookmark state.
   * The bookmark button partial toggles between create/destroy.
   * @returns {string}
   */
  _getDefaultMessage() {
    // Check if this is a create (was unbookmarked) or destroy (was bookmarked)
    // The form action indicates the current state
    const form = this.element.closest("form")
    if (!form) {
      // Fallback: Traditional Chinese (zh-TW) - should rarely be used
      // since partials always provide bookmark_toast_message_value
      return "已加入收藏"
    }

    // DELETE method = destroying bookmark (was bookmarked)
    // POST method = creating bookmark (was unbookmarked)
    const method = form.querySelector('[name="_method"]')?.value

    if (method === "delete") {
      return "已取消收藏"
    }

    return "已加入收藏"
  }
}
